import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, unwrap } from "../api/client";
import type { ClientLimits, Role, UserView } from "../api/types";
import {
  Button, Checkbox, ConfirmDialog, EmptyState, Field, FormError, Modal, PageHeader, Pill, Select, TextInput, useToast,
} from "../components/ui";
import { AccessModal } from "../components/AccessPanel";
import { ROLE_HINT, ROLE_LABEL, useSession } from "../lib/auth";
import { formatTime } from "../lib/format";
import { t } from "../lib/i18n";

const ROLES: Role[] = ["viewer", "operator", "admin", "client"];
export const DEFAULT_LIMITS: ClientLimits = { max_inputs: 3, max_outputs: 3, transcode: false };

export function LimitsFields({ value, onChange }: { value: ClientLimits; onChange: (l: ClientLimits) => void }) {
  const count = (key: "max_inputs" | "max_outputs") => (e: { target: { value: string } }) =>
    onChange({ ...value, [key]: Math.max(0, Math.min(50, Number(e.target.value) || 0)) });
  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-raised/40 p-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("Entrées au maximum")} htmlFor="u-max-in">
          <TextInput id="u-max-in" type="number" min={0} max={50} value={value.max_inputs} onChange={count("max_inputs")} />
        </Field>
        <Field label={t("Sorties au maximum")} htmlFor="u-max-out">
          <TextInput id="u-max-out" type="number" min={0} max={50} value={value.max_outputs} onChange={count("max_outputs")} />
        </Field>
      </div>
      <div>
        <Checkbox id="u-transcode" checked={value.transcode ?? false} onChange={(transcode) => onChange({ ...value, transcode })}
          label={t("Transcodage autorisé")} />
        <p className="mt-1 text-[12px] text-faint">
          {t("Vidéo H.264, habillage et conversion en progressif : chaque sortie transcodée occupe un cœur du processeur ou plus.")}
        </p>
      </div>
    </div>
  );
}

function RoleSelect({ id, value, onChange }: { id: string; value: Role; onChange: (r: Role) => void }) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value as Role)}>
      {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
    </Select>
  );
}

function UserForm({ user, onClose, onSaved }: { user?: UserView; onClose: () => void; onSaved: () => void }) {
  const editing = Boolean(user);
  const [username, setUsername] = useState(user?.username ?? "");
  const [role, setRole] = useState<Role>(user?.role ?? "operator");
  const [limits, setLimits] = useState<ClientLimits>(user?.limits ?? DEFAULT_LIMITS);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await unwrap(api.PUT("/api/users/{user_id}", {
          params: { path: { user_id: user!.id } },
          body: { role, password: password || null, limits: role === "client" ? limits : null },
        }));
      } else {
        await unwrap(api.POST("/api/users", {
          body: { username: username.trim(), password, role, limits: role === "client" ? limits : null },
        }));
      }
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={editing ? t("Modifier « {name} »", { name: user!.username }) : t("Nouveau compte")} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Annuler")}</Button>
        <Button variant="primary" type="submit" form="user-form" disabled={busy}>{editing ? t("Enregistrer") : t("Créer le compte")}</Button>
      </>}>
      <form id="user-form" onSubmit={submit} className="flex flex-col gap-4">
        {!editing && (
          <Field label={t("Identifiant")} htmlFor="u-name" hint={t("2 à 32 caractères : lettres, chiffres, . _ -")}>
            <TextInput id="u-name" required autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
        )}
        <Field label={t("Rôle")} htmlFor="u-role" hint={t(ROLE_HINT[role])}>
          <RoleSelect id="u-role" value={role} onChange={setRole} />
        </Field>
        {role === "client" && <LimitsFields value={limits} onChange={setLimits} />}
        <Field label={editing ? t("Nouveau mot de passe (facultatif)") : t("Mot de passe")} htmlFor="u-pass"
          hint={editing ? t("Laisser vide pour le garder. Un changement déconnecte l'utilisateur.") : t("10 caractères minimum. À transmettre à la personne.")}>
          <TextInput id="u-pass" type="password" autoComplete="new-password" required={!editing} minLength={10}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <FormError message={error} />
      </form>
    </Modal>
  );
}

export function Users() {
  const { me } = useSession();
  const toast = useToast();
  const [users, setUsers] = useState<UserView[] | null>(null);
  const [editing, setEditing] = useState<UserView | "new" | null>(null);
  const [deleting, setDeleting] = useState<UserView | null>(null);
  const [resetting, setResetting] = useState<UserView | null>(null);
  const [accessOf, setAccessOf] = useState<UserView | null>(null);

  const load = useCallback(() => {
    unwrap(api.GET("/api/users")).then(setUsers).catch((e: Error) => toast(e.message, "err"));
  }, [toast]);
  useEffect(load, [load]);

  const toggle = async (u: UserView) => {
    try {
      await unwrap(api.PUT("/api/users/{user_id}", { params: { path: { user_id: u.id } }, body: { disabled: !u.disabled } }));
      toast(u.disabled ? t("Compte « {name} » réactivé", { name: u.username }) : t("Compte « {name} » désactivé", { name: u.username }), u.disabled ? "ok" : "warn");
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await unwrap(api.DELETE("/api/users/{user_id}", { params: { path: { user_id: deleting.id } } }));
      toast(t("Compte « {name} » supprimé", { name: deleting.username }));
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setDeleting(null);
  };

  const resetTotp = async () => {
    if (!resetting) return;
    try {
      await unwrap(api.POST("/api/users/{user_id}/totp/reset", { params: { path: { user_id: resetting.id } } }));
      toast(t("Double authentification de « {name} » réinitialisée", { name: resetting.username }), "warn");
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setResetting(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Comptes")} subtitle={t("Qui peut se connecter à la gateway, et avec quels droits")}
        actions={<Button variant="primary" onClick={() => setEditing("new")}>{t("Nouveau compte")}</Button>} />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {ROLES.map((r) => (
          <div key={r} className="rounded-lg border border-line bg-surface px-4 py-3">
            <div className="font-cond font-semibold">{t(ROLE_LABEL[r])}</div>
            <p className="text-[13px] text-muted">{t(ROLE_HINT[r])}</p>
          </div>
        ))}
      </div>

      {users === null ? <p className="text-muted">{t("Chargement…")}</p> : users.length === 0 ? <EmptyState title={t("Aucun compte")} /> : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full min-w-[720px] text-left">
            <thead className="border-b border-line font-cond text-[12px] uppercase tracking-wider text-faint">
              <tr>
                <th className="px-4 py-2 font-semibold">{t("Compte")}</th>
                <th className="px-4 py-2 font-semibold">{t("Rôle")}</th>
                <th className="px-4 py-2 font-semibold">{t("Double auth.")}</th>
                <th className="px-4 py-2 font-semibold">{t("Dernière connexion")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{u.username}</span>
                    {u.id === me.id && <span className="ml-2 text-[12px] text-faint">{t("(vous)")}</span>}
                    {u.disabled && <span className="ml-2"><Pill tone="muted">{t("Désactivé")}</Pill></span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {t(ROLE_LABEL[u.role])}
                    {u.limits && (
                      <div className="text-[12px] text-faint">
                        {t("{inputs} entrée(s), {outputs} sortie(s)", { inputs: u.limits.max_inputs, outputs: u.limits.max_outputs })}{u.limits.transcode ? t(", transcodage") : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.totp_enabled ? <Pill tone="ok">{t("Active")}</Pill>
                      : <span className={u.role === "admin" ? "text-warn" : "text-muted"}>
                        {u.role === "admin" ? t("à activer") : t("inactive")}
                      </span>}
                  </td>
                  <td className="num px-4 py-2.5 text-muted">{u.last_login ? formatTime(u.last_login) : t("jamais")}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>{t("Modifier")}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setAccessOf(u)}>{t("Accès")}</Button>
                      {u.id !== me.id && (
                        <>
                          {u.totp_enabled && (
                            <Button size="sm" variant="ghost" onClick={() => setResetting(u)}>{t("Réinitialiser la double auth.")}</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => toggle(u)}>{u.disabled ? t("Réactiver") : t("Désactiver")}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleting(u)}>{t("Supprimer")}</Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserForm user={editing === "new" ? undefined : editing} onClose={() => setEditing(null)}
          onSaved={() => {
            toast(editing === "new" ? t("Compte créé") : t("Compte modifié"));
            setEditing(null);
            load();
          }} />
      )}
      {accessOf && <AccessModal user={accessOf} onClose={() => setAccessOf(null)} />}
      <ConfirmDialog open={deleting !== null} title={t("Supprimer le compte ?")} confirmLabel={t("Supprimer")}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {t("« {name} » ne pourra plus se connecter. Ses actions restent visibles dans le journal.", { name: deleting?.username ?? "" })}
        {deleting?.role === "client" && ` ${t("Un compte client ne se supprime qu'une fois ses entrées et sorties supprimées.")}`}
      </ConfirmDialog>
      <ConfirmDialog open={resetting !== null} title={t("Réinitialiser la double authentification ?")} confirmLabel={t("Réinitialiser")}
        onCancel={() => setResetting(null)} onConfirm={resetTotp}>
        {resetting?.role === "admin"
          ? t("À faire quand « {name} » a perdu ou changé de téléphone. Ses sessions sont fermées ; il se reconnecte avec son mot de passe et devra réactiver la double authentification aussitôt.", { name: resetting.username })
          : t("À faire quand « {name} » a perdu ou changé de téléphone. Ses sessions sont fermées ; il se reconnecte avec son mot de passe.", { name: resetting?.username ?? "" })}
      </ConfirmDialog>
    </div>
  );
}
